/**
 * @fileoverview Provide a shared dialog boundary around caller-owned workflow content. Visibility and dismissal decisions belong to the parent; the dialog owns its portal and temporary document-scroll interaction. Escape, backdrop, and close controls request the same parent dismissal. Saving or cancelling business work belongs to the enclosing workflow.
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './AppModal.css';
import AppIcon from "./AppIcon";

/**
 * onClose requests parent-owned dismissal; it does not save or roll back domain work. Visible dialogs temporarily own body-scroll locking, and teardown restores the prior style. Callers must decide what dismissal means for pending actions.
 */
function AppModal({ visible, title, description, onClose, footer, children, width, className }) {
  useEffect(

  () => {
    if (!visible) return;

    const previousOverflow = document.body.style.overflow;
    const handleKey =

    (e) => {
      if (e.key === 'Escape') onClose();
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKey);
    };
  }, [visible, onClose]);

  if (!visible || typeof document === 'undefined') return null;

  const dialogStyle = width ?
  { '--app-modal-width': typeof width === 'number' ? `${width}px` : width } :
  undefined;

  return createPortal(
    <div className="app-modal-backdrop" onClick={onClose}>
            <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        className={`app-modal${className ? ` ${className}` : ''}`}
        style={dialogStyle}
        onClick={(e) => e.stopPropagation()}>

                <div className="app-modal__header">
                    <div className="app-modal__header-copy">
                        <h2 className="app-modal__title">{title}</h2>
                        {description &&
            <p className="app-modal__description">{description}</p>}

                    </div>
                    <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="app-modal__close">

                        <AppIcon icon="pi pi-times" aria-hidden="true" />
                    </button>
                </div>
                <div className="app-modal__body">{children}</div>
                {footer && <div className="app-modal__footer">{footer}</div>}
            </div>
        </div>,
    document.body
  );
}

export default AppModal;
